import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, RefreshCw, Search, Timer, Trophy, Users } from "lucide-react";
import {
  exportScormModuleProgress,
  fetchScormModuleProgress,
  fetchScormUserProgress,
} from "../../../../../services/moduleScormReportingApi";
import ScormContentProgressPanel from "./ScormContentProgressPanel";
import ScormProgressTable from "./ScormProgressTable";
import ScormUserScormDetailModal from "./ScormUserScormDetailModal";
import "./ScormProgressReport.css";

const formatTime = (seconds) => {
  if (!seconds) return "0 min";
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
};

const formatScore = (value) => (value == null ? "-" : `${Math.round(value * 10) / 10}`);

export default function ScormProgressReportPage() {
  const { moduleId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedContentId, setSelectedContentId] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchScormModuleProgress(moduleId);
      setReport(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Impossible de charger le reporting SCORM.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [moduleId]);

  const filteredRows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (report?.collaborators || []).filter((row) => {
      const matchesContent = selectedContentId === "all" || row.contentId === selectedContentId;
      const text = `${row.fullName || ""} ${row.email || ""} ${row.contentTitle || ""} ${row.status || ""}`.toLowerCase();
      return matchesContent && (!normalized || text.includes(normalized));
    });
  }, [report, query, selectedContentId]);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await fetchScormUserProgress(moduleId, userId));
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadExport = async () => {
    const response = await exportScormModuleProgress(moduleId);
    const href = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = href;
    link.download = `scorm-progress-module-${moduleId}.csv`;
    link.click();
    URL.revokeObjectURL(href);
  };

  const completedTotal = (report?.completedCount || 0) + (report?.passedCount || 0);

  return (
    <div className="scorm-report-page">
      <div className="scorm-report-header">
        <div>
          <span>Reporting SCORM</span>
          <h1>{report?.moduleName || "Progression module"}</h1>
        </div>
        <div className="scorm-report-actions">
          <button type="button" onClick={loadReport}>
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button type="button" onClick={downloadExport} disabled={!report}>
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {error && <div className="scorm-report-alert">{error}</div>}

      <div className="scorm-kpi-grid">
        <div className="scorm-kpi">
          <Users size={18} />
          <span>Apprenants</span>
          <strong>{report?.totalLearners || 0}</strong>
        </div>
        <div className="scorm-kpi">
          <RefreshCw size={18} />
          <span>En cours</span>
          <strong>{report?.inProgressCount || 0}</strong>
        </div>
        <div className="scorm-kpi">
          <Trophy size={18} />
          <span>Terminés / réussis</span>
          <strong>{completedTotal}</strong>
        </div>
        <div className="scorm-kpi">
          <Trophy size={18} />
          <span>Score moyen</span>
          <strong>{formatScore(report?.averageScore)}</strong>
        </div>
        <div className="scorm-kpi">
          <Timer size={18} />
          <span>Temps moyen</span>
          <strong>{formatTime(report?.averageTimeSeconds)}</strong>
        </div>
      </div>

      <div className="scorm-report-layout">
        <ScormContentProgressPanel
          contents={report?.contents}
          selectedContentId={selectedContentId}
          onSelectContent={(contentId) => setSelectedContentId(selectedContentId === contentId ? "all" : contentId)}
        />

        <section className="scorm-report-main">
          <div className="scorm-toolbar">
            <label>
              <Search size={16} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher collaborateur, contenu, statut" />
            </label>
            <select
              value={selectedContentId}
              onChange={(event) => setSelectedContentId(event.target.value === "all" ? "all" : Number(event.target.value))}
            >
              <option value="all">Tous les contenus SCORM</option>
              {(report?.contents || []).map((content) => (
                <option key={content.contentId} value={content.contentId}>
                  {content.contentTitle}
                </option>
              ))}
            </select>
          </div>

          {loading ? <div className="scorm-report-empty">Chargement du reporting SCORM...</div> : <ScormProgressTable rows={filteredRows} onOpenDetail={openDetail} />}
        </section>
      </div>

      <ScormUserScormDetailModal detail={detail} loading={detailLoading} onClose={() => setDetail(null)} />
    </div>
  );
}
